/**
 * 手順 0「困る場面」（labs.md 各ラボの手順 0、learning-design.md (g)）。
 * これまでの書き方（数える、配列、removeFirst()、contains）で解かせ、間違う・遅いを数字で見せる。
 */

/** 1 文字ずつのカウンタの様子。 */
export interface CounterStep {
  index: number;
  ch: string;
  /** `(` `)` のカウンタ。 */
  round: number;
  /** `[` `]` のカウンタ。 */
  square: number;
  /** どちらかが負になった（この時点で No と決まる）。 */
  negative: boolean;
}

/** 括弧を数えて判定する（途中で負にならず、最後に両方 0 なら Yes）。`([)]` を誤って Yes にする。 */
export function countingCheck(s: string): { steps: CounterStep[]; answer: boolean } {
  let round = 0;
  let square = 0;
  let negative = false;
  const steps: CounterStep[] = [];
  [...s].forEach((ch, index) => {
    if (ch === '(') round += 1;
    else if (ch === ')') round -= 1;
    else if (ch === '[') square += 1;
    else if (ch === ']') square -= 1;
    if (round < 0 || square < 0) negative = true;
    steps.push({ index, ch, round, square, negative });
  });
  return { steps, answer: !negative && round === 0 && square === 0 };
}

export type BracketAction = 'push' | 'match' | 'mismatch' | 'empty' | 'other';

export interface BracketStep {
  index: number;
  ch: string;
  action: BracketAction;
  /** この文字を処理した後の山（添字 0 が底）。 */
  stack: string[];
}

const PAIR: Record<string, string> = { ')': '(', ']': '[' };

/**
 * 直前に開いた括弧を山で覚えて判定する（labs.md ラボ 1 手順 4、ac-algo-06-brackets）。
 * 合わない閉じ・空の山での閉じが出たらそこで止める。
 */
export function stackCheck(s: string): { steps: BracketStep[]; answer: boolean } {
  const stack: string[] = [];
  const steps: BracketStep[] = [];
  for (const [index, ch] of [...s].entries()) {
    if (ch === '(' || ch === '[') {
      stack.push(ch);
      steps.push({ index, ch, action: 'push', stack: [...stack] });
      continue;
    }
    const open = PAIR[ch];
    if (open === undefined) {
      steps.push({ index, ch, action: 'other', stack: [...stack] });
      continue;
    }
    const top = stack.at(-1);
    if (top === undefined) {
      steps.push({ index, ch, action: 'empty', stack: [] });
      return { steps, answer: false };
    }
    if (top !== open) {
      steps.push({ index, ch, action: 'mismatch', stack: [...stack] });
      return { steps, answer: false };
    }
    stack.pop();
    steps.push({ index, ch, action: 'match', stack: [...stack] });
  }
  return { steps, answer: stack.length === 0 };
}

/** 窓口の操作（labs.md ラボ 2 手順 0）。 */
export type WindowEvent = { type: 'arrive'; id: number } | { type: 'call' };

export const WINDOW_EVENTS: readonly WindowEvent[] = [
  { type: 'arrive', id: 1 },
  { type: 'arrive', id: 2 },
  { type: 'call' },
  { type: 'arrive', id: 3 },
  { type: 'call' },
  { type: 'call' },
];

export interface WindowStep {
  event: WindowEvent;
  /** 処理した後の配列。 */
  line: number[];
  /** call のとき呼ばれた人。 */
  called?: number;
  /** removeFirst() で前に詰めた人数。 */
  shifted: number;
}

/** 配列 1 本と removeFirst() で窓口を処理する。 */
export function simulateWindow(events: readonly WindowEvent[]): {
  steps: WindowStep[];
  called: number[];
  shifted: number;
} {
  const line: number[] = [];
  const called: number[] = [];
  const steps: WindowStep[] = [];
  let shifted = 0;
  for (const event of events) {
    if (event.type === 'arrive') {
      line.push(event.id);
      steps.push({ event, line: [...line], shifted: 0 });
      continue;
    }
    const front = line.shift();
    if (front === undefined) {
      steps.push({ event, line: [], shifted: 0 });
      continue;
    }
    called.push(front);
    shifted += line.length;
    steps.push({ event, line: [...line], called: front, shifted: line.length });
  }
  return { steps, called, shifted };
}

/** n 人が来てから n 回呼ぶと、removeFirst() が詰め直す人数の合計（(n − 1) + … + 0）。 */
export function removeFirstShifts(n: number): number {
  return n <= 1 ? 0 : (n * (n - 1)) / 2;
}

/** 1 秒あたりの回数の目安（complexity-meter.md の基準と同じ）。 */
export const OPS_PER_SEC = 1e8;

/** sandboxd の 1 ケースのアドレス空間（sandboxd-client.md §2.1）。 */
export const SANDBOX_MEMORY_BYTES = 512 * 1024 * 1024;

/** 番号 0〜maxId を添字にする `[Int]` の大きさ（Int は 8 バイト）。 */
export function arrayBytes(maxId: number): number {
  return (maxId + 1) * 8;
}

/** 名前と得点の組の配列を前から探す（N 人を記録、Q 回問い合わせ）。見つかるまで平均 N / 2 個見る。 */
export function linearLookupSteps(n: number, q: number): number {
  return (n * q) / 2;
}
